/**
 * WiFiQuickModal.jsx
 * 2026-02 - Modal cepat untuk ganti SSID & Password WiFi ONT pelanggan
 * dari card di halaman Manajemen Pelanggan (mobile & desktop).
 *
 * Fitur:
 * - Load settings via GET /api/customer-wifi/{device_id}/settings
 * - Save SSID/Password per WiFi network (index) via POST endpoint sama
 * - Show/hide password
 * - Indikator state per-network (memuat, simpan, sukses, error)
 * - Validasi password min 8 char (sesuai backend)
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Wifi, Eye, EyeOff, Save, X, RefreshCw, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getToken = () => localStorage.getItem('token');

const WiFiQuickModal = ({ isOpen, onClose, customer, deviceId }) => {
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [networks, setNetworks] = useState([]);
  const [forms, setForms] = useState({}); // { [index]: { ssid, password, showPw, saving } }
  const [error, setError] = useState(null);

  const fetchSettings = useCallback(async () => {
    if (!deviceId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${API_URL}/api/customer-wifi/${encodeURIComponent(deviceId)}/settings`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      if (res.data?.success) {
        const allNets = res.data.data?.wifi_networks || [];
        // Hanya tampilkan SSID yang ACTIVE (enabled !== false). SSID yang
        // dinonaktifkan disembunyikan agar UI fokus pada yang relevan.
        const nets = allNets.filter((n) => n.enabled !== false);
        setNetworks(nets);
        const init = {};
        nets.forEach((n) => {
          init[n.index] = {
            ssid: n.ssid || '',
            password: '',
            showPw: false,
            saving: false,
          };
        });
        setForms(init);
      } else {
        setError('Gagal memuat data WiFi.');
      }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) {
        setError('Device belum terdaftar di ACS atau belum pernah online.');
      } else {
        setError(e?.response?.data?.detail || 'Gagal memuat pengaturan WiFi.');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [deviceId]);

  useEffect(() => {
    if (isOpen && deviceId) {
      fetchSettings();
    }
    if (!isOpen) {
      setNetworks([]);
      setForms({});
      setError(null);
    }
  }, [isOpen, deviceId, fetchSettings]);

  const handleRefresh = async () => {
    if (!deviceId) return;
    setRefreshing(true);
    try {
      await axios.post(
        `${API_URL}/api/customer-wifi/${encodeURIComponent(deviceId)}/refresh`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success('Sedang me-refresh data dari ONT…');
      // Beri waktu ACS sync, lalu refetch
      setTimeout(() => fetchSettings(), 2500);
    } catch (e) {
      toast.error('Gagal refresh device');
      setRefreshing(false);
    }
  };

  const updateForm = (idx, patch) => {
    setForms((p) => ({ ...p, [idx]: { ...p[idx], ...patch } }));
  };

  const handleSave = async (idx) => {
    const f = forms[idx];
    if (!f) return;
    const orig = networks.find((n) => String(n.index) === String(idx));
    const ssidChanged = (f.ssid || '').trim() !== (orig?.ssid || '').trim();
    const pwProvided = (f.password || '').length > 0;

    if (!ssidChanged && !pwProvided) {
      toast.info('Tidak ada perubahan untuk disimpan');
      return;
    }
    if (pwProvided && f.password.length < 8) {
      toast.error('Password WiFi minimal 8 karakter');
      return;
    }
    if (ssidChanged && (!f.ssid || f.ssid.trim().length < 2)) {
      toast.error('SSID minimal 2 karakter');
      return;
    }

    updateForm(idx, { saving: true });
    try {
      const body = { wifi_index: String(idx) };
      if (ssidChanged) body.ssid = f.ssid.trim();
      if (pwProvided) body.password = f.password;

      const res = await axios.post(
        `${API_URL}/api/customer-wifi/${encodeURIComponent(deviceId)}/settings`,
        body,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      if (res.data?.success) {
        toast.success('Perubahan terkirim ke ONT', {
          description: 'ACS akan menerapkan perubahan dalam beberapa detik.',
          duration: 4000,
        });
        // Refetch agar data UI sync dengan device
        setTimeout(() => fetchSettings(), 1500);
      } else {
        toast.error(res.data?.message || 'Gagal menyimpan');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal menyimpan perubahan WiFi');
    } finally {
      updateForm(idx, { saving: false });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="wifi-quick-modal"
        className="max-w-md w-[95vw] max-h-[90vh] overflow-y-auto scrollbar-hide"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Wifi className="w-4 h-4 text-indigo-600" />
            Pengaturan WiFi
          </DialogTitle>
          <DialogDescription className="text-xs truncate">
            {customer?.name || 'Pelanggan'} • {customer?.pppoe_username || customer?.wan_device_id || '-'}
          </DialogDescription>
        </DialogHeader>

        {/* Refresh action */}
        <div className="flex items-center justify-between -mt-2">
          <div className="text-[11px] text-slate-500">
            {networks.length > 0
              ? `${networks.length} jaringan WiFi aktif`
              : loading ? 'Memuat data…' : ''}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleRefresh}
            disabled={loading || refreshing || !deviceId}
            data-testid="wifi-refresh-btn"
            className="h-7 px-2 text-[11px]"
          >
            <RefreshCw className={`w-3 h-3 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh dari ONT
          </Button>
        </div>

        {loading && (
          <div className="py-8 flex flex-col items-center justify-center text-slate-500 text-sm gap-2">
            <Loader2 className="w-5 h-5 animate-spin" />
            Memuat pengaturan WiFi…
          </div>
        )}

        {!loading && error && (
          <div className="py-6 text-center text-sm text-red-600 bg-red-50 dark:bg-red-900/20 rounded-md px-4">
            {error}
          </div>
        )}

        {!loading && !error && networks.length === 0 && (
          <div className="py-6 text-center text-sm text-slate-500">
            Tidak ada SSID aktif pada device ini.
            <div className="text-[11px] text-slate-400 mt-1">
              Semua jaringan WiFi sedang dinonaktifkan.
            </div>
          </div>
        )}

        {!loading &&
          !error &&
          networks.map((n) => {
            const f = forms[n.index] || { ssid: '', password: '', showPw: false, saving: false };
            const orig = networks.find((x) => String(x.index) === String(n.index));
            const ssidChanged = (f.ssid || '').trim() !== (orig?.ssid || '').trim();
            const hasPw = (f.password || '').length > 0;
            const canSave = ssidChanged || hasPw;
            return (
              <div
                key={n.index}
                className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 space-y-2 bg-slate-50/40 dark:bg-slate-800/40"
                data-testid={`wifi-network-${n.index}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-semibold text-indigo-700 dark:text-indigo-300">
                    WiFi {n.index}
                    {n.band ? ` • ${n.band}` : ''}
                  </span>
                  {n.enabled === false && (
                    <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                      Dinonaktifkan
                    </span>
                  )}
                </div>

                <div className="space-y-1">
                  <Label className="text-[11px] text-slate-600 dark:text-slate-400">Nama WiFi (SSID)</Label>
                  <Input
                    value={f.ssid}
                    onChange={(e) => updateForm(n.index, { ssid: e.target.value })}
                    placeholder="Nama WiFi"
                    className="h-9 text-sm"
                    data-testid={`wifi-ssid-${n.index}`}
                  />
                </div>

                <div className="space-y-1">
                  <Label className="text-[11px] text-slate-600 dark:text-slate-400">
                    Password Baru <span className="text-slate-400 font-normal">(kosongkan jika tidak ganti)</span>
                  </Label>
                  <div className="relative">
                    <Input
                      type={f.showPw ? 'text' : 'password'}
                      value={f.password}
                      onChange={(e) => updateForm(n.index, { password: e.target.value })}
                      placeholder="Min 8 karakter"
                      className="h-9 text-sm pr-9"
                      data-testid={`wifi-password-${n.index}`}
                    />
                    <button
                      type="button"
                      onClick={() => updateForm(n.index, { showPw: !f.showPw })}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
                      data-testid={`wifi-toggle-pw-${n.index}`}
                    >
                      {f.showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="flex justify-end pt-1">
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => handleSave(n.index)}
                    disabled={!canSave || f.saving}
                    className="h-8 bg-indigo-600 hover:bg-indigo-700"
                    data-testid={`wifi-save-${n.index}`}
                  >
                    {f.saving ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                        Menyimpan…
                      </>
                    ) : (
                      <>
                        <Save className="w-3.5 h-3.5 mr-1.5" />
                        Simpan
                      </>
                    )}
                  </Button>
                </div>
              </div>
            );
          })}

        <div className="flex justify-end pt-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onClose}
            data-testid="wifi-close-btn"
          >
            <X className="w-3.5 h-3.5 mr-1.5" />
            Tutup
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WiFiQuickModal;
